// Shopify App Bridge — CDN auto-initialized from <meta name="shopify-api-key">

declare global {
  interface Window {
    shopify?: {
      idToken(): Promise<string>;
      config: {
        apiKey: string;
        host: string;
        shop?: string;
      };
    };
  }
}

/**
 * Initialize Shopify App Bridge.
 * With CDN loading, App Bridge auto-initializes from the meta tag.
 * This function is now a no-op kept for call-site compatibility.
 */
export async function initAppBridge(): Promise<void> {
  // CDN App Bridge auto-initializes — nothing to do.
  if (!window.shopify) {
    console.warn('[hype] window.shopify not available — running outside Shopify admin');
  }
}

/**
 * Update the page title.
 * In the Polaris world, <s-page> handles the title bar natively.
 * This remains for backward compatibility during migration.
 */
export function setTitle(title: string): void {
  document.title = `${title} — Hype`;
}

/**
 * Get the session token for authenticated API calls.
 * Uses the CDN App Bridge global (window.shopify.idToken()).
 */
export async function getSessionToken(): Promise<string> {
  if (!window.shopify) {
    console.warn('[hype] No App Bridge — returning dev token');
    return 'dev-session-token';
  }
  return window.shopify.idToken();
}

/**
 * Get the merchant's shop domain (e.g. "my-store.myshopify.com").
 * Tries window.shopify.config.shop first, then decodes the `host` URL param
 * (base64 of "{shop}/admin"), then falls back to a URL query param `?shop=`.
 * Returns null if the shop domain cannot be determined.
 */
export function getShopDomain(): string | null {
  // App Bridge CDN may expose shop directly
  if (window.shopify?.config?.shop) {
    return window.shopify.config.shop;
  }

  // `host` URL param is base64url of "{shop}/admin"
  const params = new URLSearchParams(window.location.search);
  const host = params.get('host');
  if (host) {
    try {
      const decoded = atob(host.replace(/-/g, '+').replace(/_/g, '/'));
      // decoded = "{shop}/admin" — take everything before the first "/"
      const shop = decoded.split('/')[0];
      if (shop && shop.includes('.')) return shop;
    } catch {
      // ignore decode errors
    }
  }

  // Fallback: explicit ?shop= param (present during OAuth / initial install)
  const shopParam = params.get('shop');
  if (shopParam) return shopParam;

  return null;
}
